import zhEn from "../data/zhEn.json";
import enEs from "../data/enEs.json";
import api from "./api";

export const LANGS = ["zh", "en", "es"];
export const normLang = (l) => (LANGS.includes(l) ? l : "zh");

const CJK = /[\u4e00-\u9fff]/;
const LATIN = /[A-Za-z]{2,}/;
let lang = normLang(localStorage.getItem("ali_lang"));
const runtime = new Map();   // zh → en (Mongo cache + LLM)
const runtimeEs = new Map(); // zh|en → es (Mongo cache + LLM)
const pending = new Set();
const listeners = new Set();
let timer = null;

const active = () => lang !== "zh";
const mapFor = (l) => (l === "es" ? runtimeEs : runtime);

export const setI18nLang = (l) => {
  l = normLang(l);
  if (l === lang) return;
  const wasActive = active();
  lang = l;
  pending.clear();
  if (active()) { loadCache(l); if (l === "es") loadCache("en"); } // es: en cache doubles as interim text while Spanish is pending
  setTimeout(() => { if (wasActive) restoreDom(); if (active()) scheduleScan(); }, 0);
};
export const getLang = () => lang;
export const subscribeI18n = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

const cacheLoaded = { en: false, es: false };
const loadCache = async (l) => {
  if (cacheLoaded[l]) return;
  cacheLoaded[l] = true;
  try {
    const { data } = await api.get("/i18n/cache", { params: { target: l } });
    const m = mapFor(l);
    Object.entries(data.map || {}).forEach(([k, v]) => { if (v) m.set(k, v); });
    listeners.forEach((fn) => fn());
  } catch { cacheLoaded[l] = false; }
};

let inflight = 0;
const flush = async () => {
  timer = null;
  if (!active()) { pending.clear(); return; }
  if (inflight >= 3) { timer = setTimeout(flush, 150); return; }
  const target = lang;
  const m = mapFor(target);
  const texts = [];
  let chars = 0;
  for (const t of pending) {
    if (texts.length >= 40 || (chars + t.length > 3500 && texts.length)) break;
    texts.push(t); chars += t.length;
  }
  texts.forEach((t) => pending.delete(t));
  if (!texts.length) return;
  inflight += 1;
  if (pending.size && !timer) timer = setTimeout(flush, 20);
  try {
    const { data } = await api.post("/i18n/translate", { texts, target });
    Object.entries(data.map || {}).forEach(([k, v]) => m.set(k, v || k));
    texts.forEach((t) => { if (!m.has(t)) m.set(t, t); });
    listeners.forEach((fn) => fn());
  } catch {
    texts.forEach((t) => m.set(t, t));
  } finally {
    inflight -= 1;
  }
  if (pending.size && !timer) timer = setTimeout(flush, 20);
};

const queue = (s) => {
  if (!active() || s.length > 2000 || mapFor(lang).has(s) || pending.has(s)) return;
  pending.add(s);
  if (!timer) timer = setTimeout(flush, 200);
};

/* ---------- what needs translating ---------- */
const ADDR = /^\d+[\w-]*\s+[A-Za-z0-9 .'#-]+\b(ave|avenue|st|street|rd|road|dr|drive|blvd|boulevard|ln|lane|way|ct|court|pl|place|hwy|cir|circle|pkwy|ter|trl)\b/i;
const dataLike = (s) => /@|https?:\/\/|www\./i.test(s) || /^[A-Z0-9_\-#./:+ ]+$/.test(s) || ADDR.test(s) || /,\s*CA\s+9\d{4}\b/.test(s) || /^\+?\(?\d[\d\s().-]{6,}$/.test(s);
// zh→en mode: only Chinese. es mode: Chinese + English UI text (skip data-like strings: emails, URLs, codes, addresses, phones).
const needsTr = (s) => CJK.test(s) || (lang === "es" && LATIN.test(s) && !dataLike(s));

const dict = (s) => {
  if (lang === "en") return runtime.get(s) || zhEn[s] || null;
  const d = runtimeEs.get(s) || enEs[s];
  if (d) return d;
  if (CJK.test(s)) {
    const en = runtime.get(s) || zhEn[s];
    if (en) return enEs[en] || runtimeEs.get(en) || null;
  }
  return null;
};
// While a zh→es translation is in flight, Spanish readers see English rather than Chinese.
const interim = (s) => (lang === "es" && CJK.test(s) ? runtime.get(s) || zhEn[s] || null : null);

/* ---------- DOM safety net: translate rendered text nodes / placeholder / title when lang !== "zh" ---------- */
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "TEXTAREA", "INPUT", "NOSCRIPT", "CODE"]);
const translatedNodes = new Map(); // node → original text
const lastSet = new WeakMap();     // node → value we wrote (detects React overwrites)
const lastAttr = new WeakMap();    // el → {attr: value we wrote}
const ATTRS = ["placeholder", "title"];
let observer = null;
let scanTimer = null;

const NUM = /\d[\d,.:/%-]*/g;
const normalize = (s) => {
  const vals = [];
  const key = s.replace(NUM, (m) => { vals.push(m); return `{${vals.length - 1}}`; });
  return [key, vals];
};

const lookup = (s, allowQueue = true) => {
  const hit = dict(s);
  if (hit) return hit === s ? null : hit;
  if (s.length <= 60 && /\d/.test(s)) {
    const [key, vals] = normalize(s);
    if (key !== s) {
      const ph = dict(key);
      if (ph) return ph === key ? null : fill(ph, vals);
      if (allowQueue) queue(key);
      return interim(s);
    }
  }
  if (allowQueue) queue(s);
  return interim(s);
};

const skipEl = (el) => !el || SKIP_TAGS.has(el.tagName) || el.closest("[data-no-i18n],[contenteditable='true'],[contenteditable='']");

const translateText = (node) => {
  const cur = node.nodeValue;
  let raw = cur;
  if (translatedNodes.has(node)) {
    if (cur === lastSet.get(node)) raw = translatedNodes.get(node);
    else translatedNodes.delete(node);
  }
  if (!raw || !needsTr(raw)) return;
  if (skipEl(node.parentElement)) return;
  const s = raw.trim();
  if (!s) return;
  const hit = lookup(s, s.length > 1);
  if (!hit) return;
  const val = raw.replace(s, hit);
  if (val === cur) return;
  translatedNodes.set(node, raw);
  lastSet.set(node, val);
  node.nodeValue = val;
};

const translateAttrs = (el) => {
  if (skipEl(el.parentElement)) return;
  ATTRS.forEach((a) => {
    const cur = el.getAttribute(a);
    if (!cur) return;
    let raw = cur;
    if (el.hasAttribute(`data-i18n-${a}`)) {
      if (cur === lastAttr.get(el)?.[a]) raw = el.getAttribute(`data-i18n-${a}`);
      else el.removeAttribute(`data-i18n-${a}`);
    }
    if (!needsTr(raw)) return;
    const hit = lookup(raw.trim());
    if (!hit) return;
    const val = raw.replace(raw.trim(), hit);
    if (val === cur) return;
    el.setAttribute(`data-i18n-${a}`, raw);
    lastAttr.set(el, { ...(lastAttr.get(el) || {}), [a]: val });
    el.setAttribute(a, val);
  });
};

const scanTree = (root) => {
  if (!root) return;
  if (root.nodeType === Node.TEXT_NODE) { translateText(root); return; }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  translateAttrs(root);
  root.querySelectorAll?.("[placeholder],[title]").forEach(translateAttrs);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let n;
  while ((n = walker.nextNode())) translateText(n);
};

const scheduleScan = () => {
  if (scanTimer || !active()) return;
  scanTimer = setTimeout(() => { scanTimer = null; if (active() && document.body) scanTree(document.body); }, 30);
};

// Put back originals we wrote; leave nodes React has since re-rendered (their value is authoritative).
const restoreDom = () => {
  translatedNodes.forEach((orig, node) => { if (node.isConnected && node.nodeValue === lastSet.get(node)) node.nodeValue = orig; });
  translatedNodes.clear();
  ATTRS.forEach((a) => document.querySelectorAll(`[data-i18n-${a}]`).forEach((el) => {
    if (el.getAttribute(a) === lastAttr.get(el)?.[a]) el.setAttribute(a, el.getAttribute(`data-i18n-${a}`));
    el.removeAttribute(`data-i18n-${a}`);
  }));
};

/** Mount once (AppProvider). Observes DOM mutations and translates leftover text when lang !== "zh". */
export function startDomI18n() {
  if (observer || typeof MutationObserver === "undefined") return;
  observer = new MutationObserver((muts) => {
    if (!active()) return;
    for (const m of muts) {
      if (m.type === "characterData") translateText(m.target);
      else if (m.type === "attributes") translateAttrs(m.target);
      else m.addedNodes.forEach(scanTree);
    }
    if (translatedNodes.size > 8000) translatedNodes.forEach((_, n) => { if (!n.isConnected) translatedNodes.delete(n); });
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
  listeners.add(scheduleScan);
  if (active()) { loadCache(lang); if (lang === "es") loadCache("en"); scheduleScan(); }
}

const fill = (s, args) => (args && args.length ? s.replace(/\{(\d+)\}/g, (_, i) => (args[i] ?? "")) : s);

/** Translate a UI/backend string into the active language (en / es). Placeholders {0},{1} filled from args. */
export function T(key, args) {
  if (key == null || key === "") return key;
  const s = typeof key === "string" ? key : String(key);
  if (!active() || !needsTr(s)) return fill(s, args);
  const hit = dict(s);
  if (hit) return fill(hit, args);
  queue(s);
  return fill(interim(s) || s, args);
}

/** Pick field by language: pickL(obj, "name", "en") → obj.en (in en/es mode, if present) else obj.name. */
export const pickL = (obj, zhKey, enKey) => (obj ? (active() && obj[enKey] ? obj[enKey] : obj[zhKey]) : "");

const esOf = (s) => enEs[s] || runtimeEs.get(s) || s;

/** Bilingual concatenated label like "移动厕所 Portable Toilet": in en/es mode keep only the Latin part (es: then dictionary). */
export function TB(s) {
  if (!s || !active()) return s;
  const str = String(s);
  if (!CJK.test(str)) return lang === "es" ? esOf(str) : str;
  const latin = str.replace(/[\u4e00-\u9fff\u3000-\u303f\uff00-\uffef]+/g, " ").replace(/\s+/g, " ").trim();
  if (latin.length < 3) return T(str);
  return lang === "es" ? esOf(latin) : latin;
}

/** English-keyed system labels (task statuses, wait types, required file types): zh mode → Chinese via map; en passes through; es via dictionary. */
export function TE(s, zhMap) {
  if (!s) return s;
  const str = String(s);
  if (!active()) return (zhMap && zhMap[str]) || str;
  return lang === "es" ? esOf(str) : str;
}

/** Name-safe translate: dictionaries only (e.g. "未指派" → "Unassigned"); real person names pass through untouched. */
export function TN(s) {
  if (!s || !active()) return s;
  const str = String(s);
  if (lang === "en") return runtime.get(str) || zhEn[str] || str;
  const en = zhEn[str] || runtime.get(str);
  return runtimeEs.get(str) || enEs[str] || (en ? esOf(en) : str);
}

/** Admin dictionary correction (zh→en): apply immediately to memory + already-translated DOM nodes, then re-render T() users. */
export function applyOverride(zh, en) {
  if (!zh) return;
  if (en) runtime.set(zh, en); else runtime.delete(zh);
  if (lang === "en") {
    translatedNodes.forEach((raw, node) => {
      if (raw.trim() !== zh || !node.isConnected) return;
      const val = en ? raw.replace(zh, en) : raw;
      lastSet.set(node, val);
      node.nodeValue = val;
      if (!en) translatedNodes.delete(node);
    });
  }
  listeners.forEach((fn) => fn());
}
